// Shared per-primitive Domain tab-strip.
//
// A footer band of square tiles — a "Full" tile plus one per registered
// selection — that scopes a primitive's rendering to a selection's bounds.
// Used by the Slices and Contour panes; the section registry rebuilds those
// panes on selection add/delete/relabel, so callers just re-render tiles from
// live store state.
//
// The per-folder lifecycle (caching across pane child disposal, pinning
// to the folder body bottom, UI_PANE_REFRESH wiring) stays in each pane
// builder — only the skeleton, render, and selected-sync are shared here.

import { store, params as storeParams } from '@store/instance';
import { INTENTS } from '@store/intents';
import { primitiveDomainKey, type DomainScope } from '@store/selections';
import type { PrimitiveKey, Selection, SelectionId } from '@store/schema';

export interface DomainStripRefs {
    /** Outer wrapper (side label + strip) appended once to the folder body;
     *  survives pane child disposal between rebuilds. */
    container: HTMLDivElement;
    /** Inner flex row holding the "Full" tile + one tile per selection. */
    stripEl: HTMLDivElement;
}

/** Per-primitive strip behavior. Slices/Contour pass nothing (scope-only:
 *  every render derives from the chosen domain, so the chosen tile is always
 *  "active"). Volume passes all three to drive the single-active glow toggle. */
export interface DomainStripOpts {
    /** Render a rail-style glow dot in each tile. */
    showLight?: boolean;
    /** Gate the "active" highlight behind extra state (volume: enabled). When
     *  it returns false no tile reads as active even though a domain is stored. */
    activeGate?: () => boolean;
    /** Override the default `SELECTION_SET_DOMAIN` dispatch with custom click
     *  policy (volume: enable/switch/toggle-off reconciliation). */
    onTileClick?: (id: SelectionId | 'full') => void;
}

// Keyed by primitive so `renderDomainStrip` / `syncDomainSelected` — called by
// name from each pane's refresh listener — can reach the gate without threading
// opts through every call. One strip per primitive, so a Map is enough.
const OPTS_BY_PRIMITIVE = new Map<DomainScope, DomainStripOpts>();

/** A tile is "active" when it matches the stored domain AND any extra gate
 *  (volume's enabled flag) is satisfied. */
function isTileActive(primitive: DomainScope, id: string, activeDomain: string): boolean {
    if (id !== activeDomain) return false;
    const gate = OPTS_BY_PRIMITIVE.get(primitive)?.activeGate;
    return gate ? gate() : true;
}

const SELECTION_ICON_BY_KIND: Record<Selection['kind'], string> = {
    box: 'fa-cube',
    sphere: 'fa-circle',
};

function escapeAttr(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Render the strip's tiles ("Full" + one per selection) from live store
 *  state. Called on first build and on every rebuild — selections come and go
 *  and the active domain can change externally. */
export function renderDomainStrip(stripEl: HTMLDivElement, primitive: DomainScope): void {
    const activeDomain = String(storeParams[primitiveDomainKey(primitive)] ?? 'full');
    const order = storeParams.selectionOrder;
    const light = OPTS_BY_PRIMITIVE.get(primitive)?.showLight
        ? '<span class="domain-strip-tab-light" aria-hidden="true"></span>'
        : '';
    const tiles: string[] = [];
    // "Full" tile — always first, always present. fa-border-all reads as "the
    // whole grid" (no spatial restriction).
    const fullActive = isTileActive(primitive, 'full', activeDomain);
    tiles.push(
        `<button type="button" role="tab" class="domain-strip-tab" `
      + `data-domain-id="full" `
      + `aria-selected="${fullActive ? 'true' : 'false'}" `
      + `aria-label="Full domain" title="Full domain (no spatial restriction)">`
      + `<i class="fas fa-border-all" aria-hidden="true"></i>`
      + `<span class="domain-strip-tab-label">Full</span>`
      + light
      + `</button>`,
    );
    for (const id of order) {
        const sel = storeParams.selections[id];
        if (!sel) continue;
        const icon = SELECTION_ICON_BY_KIND[sel.kind];
        const isActive = isTileActive(primitive, id, activeDomain);
        const labelEsc = escapeAttr(sel.label);
        const titleEsc = escapeAttr(`Restrict to: ${sel.label}`);
        tiles.push(
            `<button type="button" role="tab" class="domain-strip-tab" `
          + `data-domain-id="${id}" `
          + `aria-selected="${isActive ? 'true' : 'false'}" `
          + `aria-label="${labelEsc}" title="${titleEsc}">`
          + `<i class="fas ${icon}" aria-hidden="true"></i>`
          + `<span class="domain-strip-tab-label">${labelEsc}</span>`
          + light
          + `</button>`,
        );
    }
    stripEl.innerHTML = tiles.join('');
}

/** Light sync: flip `aria-selected` on every domain tile for `domainKey` in
 *  the document. Faster + no flicker compared to a full re-render.
 *
 *  Queries the whole document rather than a captured strip so an orphaned
 *  strip from a prior build can't keep a stale `aria-selected="true"`. Scoped
 *  to this primitive via `data-domain-for` so a Slices refresh doesn't touch
 *  the Contour strip and vice versa. */
export function syncDomainSelected(primitive: DomainScope): void {
    const active = String(storeParams[primitiveDomainKey(primitive)] ?? 'full');
    document.querySelectorAll<HTMLButtonElement>(`.domain-strip[data-domain-for="${primitive}"] .domain-strip-tab`).forEach((btn) => {
        const id = btn.dataset['domainId'] ?? 'full';
        btn.setAttribute('aria-selected', isTileActive(primitive, id, active) ? 'true' : 'false');
    });
}

/** Build the persistent domain-strip skeleton (vertical "DOMAIN" side label +
 *  tab row) and wire click delegation. Called once per folder lifetime. */
export function buildDomainStrip(primitive: DomainScope, opts?: DomainStripOpts): DomainStripRefs {
    if (opts) OPTS_BY_PRIMITIVE.set(primitive, opts);
    const onTileClick = opts?.onTileClick;
    const container = document.createElement('div');
    container.className = 'domain-strip';
    container.dataset['domainFor'] = primitive;
    container.setAttribute('aria-label', 'Domain');

    const sideLabel = document.createElement('div');
    sideLabel.className = 'domain-strip-side-label';
    sideLabel.setAttribute('aria-hidden', 'true');
    sideLabel.textContent = 'DOMAIN';

    const stripEl = document.createElement('div');
    stripEl.className = 'domain-strip-tabs';
    stripEl.setAttribute('role', 'tablist');
    // Click delegation — one listener handles every tile regardless of how
    // many selections come and go on subsequent re-renders.
    stripEl.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.domain-strip-tab');
        if (!btn) return;
        const id = btn.dataset['domainId'];
        if (!id) return;
        const next = id === 'full' ? 'full' : (id as SelectionId);
        // Drop focus so the browser's focus ring doesn't read as a second
        // "selected" indicator.
        btn.blur();
        if (onTileClick) {
            // Custom policy (volume toggle-off may clear every tile). Don't
            // optimistically flip aria-selected here — let the dispatched
            // intents' UI_PANE_REFRESH → syncDomainSelected settle it, so we
            // never flash a glow on a tile that's about to turn off.
            onTileClick(next);
            return;
        }
        // Default scope-only behavior: enforce single-selection visually in the
        // same tick, scoped to this primitive's strip (catches any orphan from a
        // prior build that might still hold a stale `aria-selected="true"`).
        container.querySelectorAll<HTMLButtonElement>('.domain-strip-tab').forEach((b) => {
            b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
        });
        // The default (no onTileClick) path only ever runs for render primitives;
        // the 'reductions' scope always supplies onTileClick and returns above.
        store.dispatch(INTENTS.SELECTION_SET_DOMAIN, { primitive: primitive as PrimitiveKey, selectionId: next });
    });

    container.append(sideLabel, stripEl);
    renderDomainStrip(stripEl, primitive);
    return { container, stripEl };
}
